/**
 * calendarMath.ts — pure month-grid math for the Field Schedule calendar
 * (M5b). No DOM, no fetch, no library (decision M5b): the mockup dodges
 * month math entirely by hardcoding one July-2026 grid
 * (klaxiora-crm-mockup-full.html :1852-1908); this module is the real
 * version of that grid for ANY month.
 *
 * Shape rules, matching the mockup's grid: MONDAY-start weeks; full weeks
 * only, so a month renders 4-6 rows with leading/trailing other-month
 * cells (the mockup's .other-month); "today" comes from an injected clock,
 * never a hardcoded date (the mockup pins 2026-07-27). gridStart/gridEnd
 * bound the whole visible grid inclusive — the windowed
 * GET /v1/operations/tasks?from=&to= fetch uses them, so pins on
 * other-month edge cells are real, not blank theatre.
 *
 * All date arithmetic runs through local-time Date construction with day
 * overflow (new Date(y, m-1, d) normalizes), which is DST-safe — never
 * add 86 400 000 ms to a timestamp to move a calendar day.
 */

export interface CalendarCell {
  date: string   // "YYYY-MM-DD"
  day: number    // 1-31, the number the cell renders
  inMonth: boolean
  isToday: boolean
}

export interface CalendarMonth {
  year: number
  month: number  // 1-12
  label: string  // "July 2026"
  cells: CalendarCell[]     // length 28-42, a multiple of 7
  gridStart: string          // first cell's date (inclusive)
  gridEnd: string            // last cell's date (inclusive)
}

function toISO(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** The year+month pair containing `now` — initial state and the Today jump. */
export function monthOf(now: Date): { year: number; month: number } {
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

export function prevMonth(year: number, month: number): { year: number; month: number } {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 }
}

export function nextMonth(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 }
}

export function monthGrid(year: number, month: number, now: Date): CalendarMonth {
  const first = new Date(year, month - 1, 1)
  const lead = (first.getDay() + 6) % 7 // other-month days before the 1st; Monday-start
  const daysInMonth = new Date(year, month, 0).getDate()
  const totalCells = Math.ceil((lead + daysInMonth) / 7) * 7
  const today = toISO(now)

  const cells: CalendarCell[] = []
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(year, month - 1, 1 - lead + i)
    const date = toISO(d)
    cells.push({
      date,
      day: d.getDate(),
      inMonth: d.getMonth() === month - 1 && d.getFullYear() === year,
      isToday: date === today,
    })
  }

  return {
    year,
    month,
    label: first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    cells,
    gridStart: cells[0].date,
    gridEnd: cells[cells.length - 1].date,
  }
}
